import React, { useEffect, useRef, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import useElementToPdf from '../utils/useElementToPdf';
import { useLanguage } from '../contexts/LanguageContext';
import api, { trackVisit } from '../utils/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import {
  Crown,
  Award,
  Star,
  Sparkles,
  Gem,
  ShieldCheck,
  Printer,
  AlertTriangle,
  Calendar,
  TrendingUp,
  Loader2,
  ArrowRight,
  RefreshCw,
  FileText,
  Clock,
  Phone,
  Eye,
  ScanLine,
} from 'lucide-react';
import BackToHome from '../components/BackToHome';
import LoyaltyTermsSheet from '../components/LoyaltyTermsSheet';
import LoyaltyQRScanner from '../components/loyalty/LoyaltyQRScanner';
import { playScanSuccess, playScanError, primeAudio } from '../utils/scanSounds';

const LEVEL_ICONS = {
  bronze: Award,
  silver: Star,
  gold: Sparkles,
  black_elite: Gem,
};

// ---------- Step components ----------

const Step1Lookup = ({ isArabic, onNext, onPending }) => {
  const [num, setNum] = useState('');
  const [last4, setLast4] = useState('');
  const [loading, setLoading] = useState(false);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [validating, setValidating] = useState(false);

  const handleOpenScanner = () => {
    // Unlock audio context as part of the user gesture
    try { primeAudio(); } catch (err) {
      console.debug('[LoyaltyPublic] primeAudio failed (audio may be blocked):', err?.message);
    }
    setScannerOpen(true);
  };

  const handleScanned = async (membershipNumber, rawText) => {
    setScannerOpen(false);
    setValidating(true);
    try {
      const res = await api.get(`/loyalty/qr-validate/${encodeURIComponent(membershipNumber)}`);
      if (res.data?.valid) {
        playScanSuccess();
        setNum(membershipNumber);
        toast.success(
          isArabic
            ? `تم التعرف على البطاقة #${membershipNumber} ✓ — أكمل بإدخال آخر 4 أرقام جوال`
            : `Card #${membershipNumber} recognized ✓ — enter last 4 mobile digits`
        );
      } else {
        playScanError();
        toast.error(
          isArabic
            ? 'الرمز الممسوح لا ينتمي لنظامنا'
            : 'Scanned code does not belong to our system'
        );
      }
    } catch (_) {
      playScanError();
      toast.error(isArabic ? 'تعذّر التحقق من الرمز' : 'Could not validate code');
    } finally {
      setValidating(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!num.trim() || !last4.trim() || last4.length !== 4) {
      toast.error(isArabic ? 'الرجاء إدخال رقم العضوية وآخر 4 أرقام جوال' : 'Enter membership # and last 4 digits');
      return;
    }
    setLoading(true);
    try {
      const res = await api.post('/loyalty/lookup', {
        membership_number: num.trim(),
        phone_last4: last4.trim(),
      });
      onNext(res.data);
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      // Structured pending/inactive/rejected response → render persistent status card
      if (status === 403 && detail && typeof detail === 'object' && detail.code) {
        onPending({
          code: detail.code,
          membership_number: detail.membership_number || num.trim(),
          message_ar: detail.message_ar,
          message_en: detail.message_en,
        });
      } else {
        // Fallback (string-based legacy or 404 generic)
        toast.error(
          (typeof detail === 'string' ? detail : null) ||
            (isArabic ? 'بيانات غير صحيحة' : 'Invalid')
        );
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
    <Card className="p-6 bg-card border-white/10 max-w-md w-full mx-auto" dir={isArabic ? 'rtl' : 'ltr'}>
      <div className="flex flex-col items-center mb-6">
        <div className="w-16 h-16 bg-gradient-to-br from-amber-400 to-orange-500 rounded-2xl flex items-center justify-center shadow-lg mb-3">
          <Crown className="w-8 h-8 text-white" />
        </div>
        <h2 className="text-xl font-bold">{isArabic ? 'بطاقة الولاء' : 'Loyalty Card'}</h2>
        <p className="text-xs text-muted-foreground mt-1 text-center">
          {isArabic ? 'أدخل رقم عضويتك وآخر 4 أرقام من جوالك للوصول لبطاقتك' : 'Enter your membership # and last 4 mobile digits'}
        </p>
      </div>

      {/* زر المسح السريع */}
      <Button
        type="button"
        variant="outline"
        onClick={handleOpenScanner}
        disabled={validating}
        className="w-full gap-2 mb-3 border-amber-500/40 hover:bg-amber-500/10"
        data-testid="loyalty-public-scan-btn"
      >
        {validating ? <Loader2 className="w-4 h-4 animate-spin" /> : <ScanLine className="w-4 h-4 text-amber-500" />}
        {isArabic ? 'مسح رمز QR بدلاً من إدخال رقم العضوية' : 'Scan QR code instead of typing'}
      </Button>
      <div className="flex items-center gap-2 mb-3">
        <div className="flex-1 h-px bg-white/10" />
        <span className="text-[10px] text-muted-foreground">{isArabic ? 'أو' : 'or'}</span>
        <div className="flex-1 h-px bg-white/10" />
      </div>

      <form onSubmit={submit} className="space-y-3" data-testid="loyalty-lookup-form">
        <div>
          <label className="text-xs text-muted-foreground mb-1 block">{isArabic ? 'رقم العضوية' : 'Membership #'}</label>
          <Input value={num} onChange={(e) => setNum(e.target.value)} placeholder="100001" data-testid="lookup-membership" />
        </div>
        <div>
          <label className="text-xs text-muted-foreground mb-1 block">{isArabic ? 'آخر 4 أرقام من الجوال' : 'Last 4 digits'}</label>
          <Input value={last4} maxLength={4} onChange={(e) => setLast4(e.target.value.replace(/\D/g, '').slice(0, 4))} placeholder="1234" data-testid="lookup-last4" />
        </div>
        <Button type="submit" className="w-full gap-2" disabled={loading} data-testid="lookup-submit">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
          {isArabic ? 'متابعة' : 'Continue'}
        </Button>
      </form>
    </Card>
    <LoyaltyQRScanner
      isOpen={scannerOpen}
      onClose={() => setScannerOpen(false)}
      onDetected={handleScanned}
      isArabic={isArabic}
    />
    </>
  );
};

const Step2Captcha = ({ isArabic, captchaData, onVerified, onBack }) => {
  const [a1, setA1] = useState('');
  const [a2, setA2] = useState('');
  const [loading, setLoading] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(captchaData.expires_in_seconds || 300);

  useEffect(() => {
    const t = setInterval(() => setSecondsLeft((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(t);
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.post('/loyalty/verify', {
        captcha_id: captchaData.captcha_id,
        answer1: parseInt(a1),
        answer2: parseInt(a2),
      });
      onVerified(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || (isArabic ? 'إجابة خاطئة' : 'Wrong answer'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="p-6 bg-card border-white/10 max-w-md w-full mx-auto" dir={isArabic ? 'rtl' : 'ltr'}>
      <div className="flex flex-col items-center mb-4">
        <ShieldCheck className="w-10 h-10 text-emerald-500 mb-2" />
        <h2 className="text-lg font-bold">{isArabic ? 'تأكيد الهوية' : 'Identity check'}</h2>
        <p className="text-xs text-muted-foreground text-center mt-1">
          {isArabic ? `أجب عن السؤالين التاليين خلال ${secondsLeft} ثانية` : `Answer the questions within ${secondsLeft}s`}
        </p>
      </div>
      <form onSubmit={submit} className="space-y-3" data-testid="captcha-form">
        <div>
          <label className="text-sm font-mono">
            {captchaData.questions[0]} = ?
          </label>
          <Input type="number" value={a1} onChange={(e) => setA1(e.target.value)} required data-testid="captcha-a1" />
        </div>
        <div>
          <label className="text-sm font-mono">
            {captchaData.questions[1]} = ?
          </label>
          <Input type="number" value={a2} onChange={(e) => setA2(e.target.value)} required data-testid="captcha-a2" />
        </div>
        <div className="flex gap-2">
          <Button type="button" variant="outline" onClick={onBack} className="flex-1">{isArabic ? 'رجوع' : 'Back'}</Button>
          <Button type="submit" disabled={loading || secondsLeft === 0} className="flex-1 gap-1" data-testid="verify-submit">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
            {isArabic ? 'عرض البطاقة' : 'Show Card'}
          </Button>
        </div>
      </form>
    </Card>
  );
};

// ---------- Print sheet (A4) ----------
const CardPrintSheet = React.forwardRef(({ data, isArabic }, ref) => {
  const Icon = LEVEL_ICONS[data.level.key] || Award;
  return (
    <div
      ref={ref}
      style={{
        padding: '14mm', fontFamily: 'Cairo, Tajawal, Arial, sans-serif',
        background: '#fff', color: '#111', width: '210mm', minHeight: '297mm', boxSizing: 'border-box',
      }}
      dir={isArabic ? 'rtl' : 'ltr'}
    >
      <div style={{ textAlign: 'center', marginBottom: 24 }}>
        <h1 style={{ margin: 0, fontSize: 22 }}>KE Gents Saloon</h1>
        <h2 style={{ margin: '4px 0', fontSize: 14 }}>بطاقة الولاء / Loyalty Card</h2>
      </div>

      <div style={{
        background: data.level.color, color: '#fff', borderRadius: 16, padding: 20, marginBottom: 18,
        boxShadow: '0 4px 16px rgba(0,0,0,.12)',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: 11, opacity: .85 }}>عضوية / Membership</div>
            <div style={{ fontSize: 28, fontWeight: 800, letterSpacing: 2 }}>#{data.membership_number}</div>
            <div style={{ fontSize: 11, marginTop: 6 }}>
              منذ / Since: {(data.joined_at || '').slice(0, 10)}
            </div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <Icon size={36} />
            <div style={{ fontSize: 13, fontWeight: 700, marginTop: 4 }}>
              {data.level.name_ar} / {data.level.name_en}
            </div>
            <div style={{ fontSize: 10, opacity: .85 }}>+{data.level.bonus_pct}% بونص</div>
          </div>
        </div>
        {/* QR code for barber scanning */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 14, marginTop: 14, paddingTop: 12, borderTop: '1px solid rgba(255,255,255,.25)' }}>
          <div style={{ background: '#fff', padding: 6, borderRadius: 6, lineHeight: 0 }}>
            <QRCodeSVG
              value={String(data.membership_number)}
              size={70}
              bgColor="#ffffff"
              fgColor="#000000"
              level="M"
              marginSize={0}
            />
          </div>
          <div style={{ textAlign: 'start', fontSize: 10, opacity: .95, maxWidth: 230 }}>
            <div style={{ fontWeight: 800, marginBottom: 3 }}>🎯 امسح الرمز / Scan to link</div>
            <div>أَرِ هذا الرمز للحلاق فيمسحه بالكاميرا فوراً</div>
            <div style={{ marginTop: 2 }}>Barber scans this QR to attach your visit</div>
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <Box label="نقاط نشطة / Active Points" value={data.points_balance.toLocaleString()} accent="#15803d" />
        <Box label="الرصيد المعادل / Credit" value={`${data.redeemable_aed} د.إ`} accent="#15803d" />
        <Box label="عدد الزيارات / Visits" value={data.visits_count} />
        <Box label="مدة العضوية / Months" value={`${data.months_active} شهر`} />
        <Box label="نقاط الزيارة الأخيرة / Last visit" value={`+${data.last_visit_points}`} accent="#1d4ed8" />
        <Box label="تنتهي قريباً / Expiring soon" value={data.expiring_soon_points} accent={data.expiring_soon_points > 0 ? '#c2410c' : undefined} />
      </div>

      {(data.streak_months || data.next_streak_milestone) && (
        <div style={{
          marginTop: 14, padding: 12, background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 12,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12,
        }}>
          <div>
            <div style={{ fontWeight: 700 }}>🔥 متتالية الولاء / Streak</div>
            <div>{data.streak_months || 0} شهر متواصل / consecutive months</div>
          </div>
          {data.next_streak_milestone && (
            <div style={{ textAlign: 'left', color: '#c2410c' }}>
              <div style={{ fontSize: 10, color: '#666' }}>المكافأة التالية / Next</div>
              <div style={{ fontWeight: 700 }}>+{data.next_streak_milestone.bonus_points} pts بعد {data.next_streak_milestone.months_to_go}m</div>
            </div>
          )}
        </div>
      )}

      {data.level.next && (
        <div style={{
          marginTop: 16, padding: 12, background: '#f3f4f6', borderRadius: 12, fontSize: 12,
        }}>
          <div style={{ fontWeight: 700 }}>المستوى التالي / Next: {data.level.next.name_ar} ({data.level.next.name_en})</div>
          <div>باقي {data.level.next.points_to_go} نقطة أو {data.level.next.months_to_go} شهر</div>
        </div>
      )}

      {data.recent_transactions && data.recent_transactions.length > 0 && (
        <div style={{ marginTop: 18 }}>
          <h3 style={{ fontSize: 13, marginBottom: 6 }}>آخر العمليات / Recent</h3>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
            <thead style={{ background: '#f3f4f6' }}>
              <tr>
                <th style={th}>التاريخ / Date</th>
                <th style={th}>النوع / Type</th>
                <th style={th}>الخدمة / Service</th>
                <th style={th}>النقاط</th>
                <th style={th}>تنتهي</th>
              </tr>
            </thead>
            <tbody>
              {data.recent_transactions.map((t, i) => (
                <tr key={`${t.created_at || ''}-${t.type}-${i}`} style={{ borderBottom: '1px solid #eee' }}>
                  <td style={td}>{(t.created_at || '').slice(0, 10)}</td>
                  <td style={{ ...td, color: t.type === 'earn' ? '#15803d' : '#1d4ed8' }}>{t.type === 'earn' ? 'كسب / Earn' : 'استبدال / Redeem'}</td>
                  <td style={td}>{t.service_name || '-'}</td>
                  <td style={{ ...td, fontWeight: 700 }}>{t.type === 'earn' ? '+' : '-'}{t.points}</td>
                  <td style={td}>{(t.expires_at || '').slice(0, 10) || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div style={{ marginTop: 26, fontSize: 10, color: '#666', textAlign: 'center' }}>
        100 نقطة = 7 د.إ رصيد داخل الصالون فقط — Member benefits as per program rules
      </div>
    </div>
  );
});
CardPrintSheet.displayName = 'CardPrintSheet';

const th = { padding: 6, border: '1px solid #ddd', textAlign: 'right', fontWeight: 700 };
const td = { padding: 6, border: '1px solid #eee', textAlign: 'right' };

const Box = ({ label, value, accent }) => (
  <div style={{ background: '#f9fafb', border: '1px solid #eee', borderRadius: 10, padding: 12 }}>
    <div style={{ fontSize: 10, color: '#666', marginBottom: 4 }}>{label}</div>
    <div style={{ fontSize: 18, fontWeight: 800, color: accent || '#111' }}>{value}</div>
  </div>
);

// ---------- Pending / Inactive / Rejected status card ----------
const STATUS_META = {
  MEMBERSHIP_PENDING: {
    title_ar: 'عضويتك قيد الاعتماد',
    title_en: 'Membership Pending Approval',
    icon: Clock,
    iconBg: 'from-amber-400 to-orange-500',
    accent: '#f59e0b',
    badge_ar: 'قيد الاعتماد',
    badge_en: 'Pending',
    hint_ar: 'يقوم مدير الصالون بمراجعة بياناتك حالياً. حال اعتماد العضوية ستصبح بطاقتك متاحة هنا مباشرة.',
    hint_en: 'The salon manager is reviewing your details. Once approved, your card will be accessible here.',
  },
  MEMBERSHIP_REJECTED: {
    title_ar: 'تم رفض هذه العضوية',
    title_en: 'Membership Rejected',
    icon: AlertTriangle,
    iconBg: 'from-red-500 to-rose-600',
    accent: '#ef4444',
    badge_ar: 'مرفوضة',
    badge_en: 'Rejected',
    hint_ar: 'لم يتم اعتماد هذه العضوية. يرجى التواصل مع الصالون للاستفسار عن سبب الرفض.',
    hint_en: 'This membership was not approved. Please contact the salon for details.',
  },
  MEMBERSHIP_INACTIVE: {
    title_ar: 'العضوية غير نشطة',
    title_en: 'Membership Inactive',
    icon: AlertTriangle,
    iconBg: 'from-gray-500 to-slate-600',
    accent: '#94a3b8',
    badge_ar: 'غير نشطة',
    badge_en: 'Inactive',
    hint_ar: 'هذه العضوية موقوفة حالياً. تواصل مع الصالون لإعادة تفعيلها.',
    hint_en: 'This membership is currently suspended. Please contact the salon to reactivate it.',
  },
};

const StepStatusCard = ({ isArabic, info, onReset }) => {
  const meta = STATUS_META[info.code] || STATUS_META.MEMBERSHIP_PENDING;
  const Icon = meta.icon;
  const title = isArabic ? meta.title_ar : meta.title_en;
  const badge = isArabic ? meta.badge_ar : meta.badge_en;
  const hint = isArabic ? meta.hint_ar : meta.hint_en;
  const mainMsg = isArabic ? info.message_ar : info.message_en;

  return (
    <Card
      className="p-6 bg-card border-2 max-w-md w-full mx-auto relative overflow-hidden"
      style={{ borderColor: meta.accent + '55', background: `linear-gradient(135deg, ${meta.accent}15, transparent 70%)` }}
      dir={isArabic ? 'rtl' : 'ltr'}
      data-testid="loyalty-status-card"
    >
      <div className="flex flex-col items-center text-center">
        <div className={`w-20 h-20 rounded-2xl bg-gradient-to-br ${meta.iconBg} flex items-center justify-center shadow-lg mb-4`}>
          <Icon className="w-10 h-10 text-white" />
        </div>

        <span
          className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-[11px] font-bold mb-3"
          style={{ background: meta.accent + '22', color: meta.accent, border: `1px solid ${meta.accent}55` }}
        >
          {badge}
        </span>

        <h2 className="text-xl font-bold mb-2" style={{ color: meta.accent }}>{title}</h2>

        {info.membership_number && (
          <div className="text-xs text-muted-foreground mb-3">
            {isArabic ? 'رقم العضوية' : 'Membership #'}: {' '}
            <span className="font-mono font-bold text-foreground">#{info.membership_number}</span>
          </div>
        )}

        <div
          className="rounded-lg p-4 text-sm leading-relaxed mb-3"
          style={{ background: meta.accent + '10', border: `1px solid ${meta.accent}33`, color: 'inherit' }}
        >
          {mainMsg}
        </div>

        <p className="text-xs text-muted-foreground mb-5 max-w-xs leading-relaxed">{hint}</p>

        <div className="flex gap-2 w-full">
          <Button
            variant="outline"
            onClick={onReset}
            className="flex-1 gap-1"
            data-testid="status-card-back-btn"
          >
            <RefreshCw className="w-4 h-4" />
            {isArabic ? 'محاولة أخرى' : 'Try again'}
          </Button>
          <a
            href="https://wa.me/971569810665"
            target="_blank"
            rel="noopener noreferrer"
            className="flex-1"
            data-testid="status-card-contact-btn"
          >
            <Button className="w-full gap-1 bg-emerald-600 hover:bg-emerald-700">
              <Phone className="w-4 h-4" />
              {isArabic ? 'تواصل معنا' : 'Contact us'}
            </Button>
          </a>
        </div>
      </div>
    </Card>
  );
};

// ---------- Step 3: card ----------

const Step3Card = ({ isArabic, data, onReset }) => {
  const printRef = useRef(null);
  const handlePrint = useElementToPdf({
    contentRef: printRef,
    documentTitle: `loyalty_card_${data.membership_number}`,
    pageStyle: '@page { size: A4 portrait; margin: 8mm; } @media print { body { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; } }',
  });

  const Icon = LEVEL_ICONS[data.level.key] || Award;

  return (
    <div className="space-y-4 max-w-4xl mx-auto" dir={isArabic ? 'rtl' : 'ltr'}>
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-gradient-to-br from-amber-400 to-orange-500 rounded-xl flex items-center justify-center">
            <Crown className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-2xl font-bold">{isArabic ? 'بطاقتك' : 'Your Card'}</h2>
            <p className="text-xs text-muted-foreground">{isArabic ? 'لا تظهر الأسماء أو الأرقام لحماية خصوصيتك' : 'Anonymous view'}</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={onReset} className="gap-1" data-testid="reset-btn">
            <RefreshCw className="w-4 h-4" />
            {isArabic ? 'بطاقة أخرى' : 'New lookup'}
          </Button>
          <Button onClick={handlePrint} className="gap-1 bg-emerald-600 hover:bg-emerald-700" data-testid="print-card-btn">
            <Printer className="w-4 h-4" />
            {isArabic ? 'تنزيل PDF (A4)' : 'Download PDF (A4)'}
          </Button>
        </div>
      </div>

      {/* Live card */}
      <Card
        className="p-6 border-2 relative overflow-hidden"
        style={{
          background: `linear-gradient(135deg, ${data.level.color}15, transparent 70%)`,
          borderColor: data.level.color + '55',
        }}
      >
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
          <div>
            <div className="text-xs text-muted-foreground">{isArabic ? 'رقم العضوية' : 'Membership #'}</div>
            <div className="text-3xl font-mono font-bold tracking-widest" style={{ color: data.level.color }}>#{data.membership_number}</div>
            <div className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
              <Calendar className="w-3 h-3" />
              {isArabic ? 'منذ:' : 'Since:'} {(data.joined_at || '').slice(0, 10)} ({data.months_active} {isArabic ? 'شهر' : 'months'})
            </div>
          </div>
          <div className="flex items-center gap-2 px-4 py-2 rounded-full font-bold" style={{ background: data.level.color, color: '#fff' }}>
            <Icon className="w-5 h-5" />
            {isArabic ? data.level.name_ar : data.level.name_en}
            <span className="text-xs opacity-90">+{data.level.bonus_pct}%</span>
          </div>
        </div>

        {/* QR code for the barber to scan from the customer's phone */}
        <div className="mt-4 pt-4 border-t border-white/10 flex flex-col sm:flex-row items-center justify-center gap-4">
          <div className="bg-white p-2 rounded-lg" data-testid="member-qr-code">
            <QRCodeSVG
              value={String(data.membership_number)}
              size={108}
              bgColor="#ffffff"
              fgColor="#000000"
              level="M"
              marginSize={0}
            />
          </div>
          <div className="text-center sm:text-start max-w-xs">
            <div className="text-xs font-bold text-emerald-400 mb-1">
              {isArabic ? '🎯 رمز سريع للحلاق' : '🎯 Quick scan code'}
            </div>
            <div className="text-[11px] text-muted-foreground leading-relaxed">
              {isArabic
                ? 'أَرِ هذا الرمز للحلاق ليمسحه بالكاميرا ويربط الإيراد ببطاقتك تلقائياً.'
                : 'Show this code to your barber. They scan it and link your visit instantly.'}
            </div>
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <StatBox label={isArabic ? 'نقاط نشطة' : 'Active Points'} value={data.points_balance.toLocaleString()} className="text-emerald-500" />
        <StatBox label={isArabic ? 'الرصيد' : 'Credit'} value={`${data.redeemable_aed} د.إ`} className="text-emerald-500" />
        <StatBox label={isArabic ? 'الزيارات' : 'Visits'} value={data.visits_count} />
        <StatBox label={isArabic ? 'نقاط الزيارة الأخيرة' : 'Last Visit Points'} value={`+${data.last_visit_points}`} className="text-blue-500" />
        <StatBox label={isArabic ? 'تنتهي قريباً' : 'Expiring soon'} value={data.expiring_soon_points} className={data.expiring_soon_points > 0 ? 'text-amber-500' : ''} icon={data.expiring_soon_points > 0 ? AlertTriangle : null} />
        <StatBox label={isArabic ? 'إجمالي مكتسبة' : 'Total earned'} value={data.total_points_earned.toLocaleString()} />
      </div>

      {/* Streak panel */}
      {(data.streak_months || data.next_streak_milestone) && (
        <Card className="p-4 bg-card border-orange-500/30 bg-gradient-to-r from-orange-500/5 to-transparent">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <div className="text-sm flex items-center gap-2 font-semibold">
                <span className="text-2xl">🔥</span>
                {isArabic
                  ? `متتالية الولاء: ${data.streak_months || 0} شهر متواصل`
                  : `Loyalty streak: ${data.streak_months || 0} consecutive months`}
              </div>
              {data.streak_started_at && (
                <div className="text-[10px] text-muted-foreground mt-1">
                  {isArabic ? 'بدأت:' : 'Since:'} {(data.streak_started_at || '').slice(0, 10)}
                </div>
              )}
            </div>
            {data.next_streak_milestone && (
              <div className="text-end">
                <div className="text-xs text-muted-foreground">
                  {isArabic ? 'المكافأة التالية' : 'Next reward'}
                </div>
                <div className="text-sm font-bold text-orange-500">
                  +{data.next_streak_milestone.bonus_points} {isArabic ? 'نقطة' : 'pts'} {isArabic ? 'بعد' : 'after'} {data.next_streak_milestone.months_to_go} {isArabic ? 'شهر' : 'm'}
                </div>
              </div>
            )}
          </div>
        </Card>
      )}

      {/* Referral stats panel — totals only, never names */}
      {data.referral_stats && (data.referral_stats.referred_count > 0) && (
        <Card className="p-4 bg-card border-amber-500/30 bg-gradient-to-r from-amber-500/5 to-transparent" data-testid="referral-stats-card">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-2xl">🎁</span>
            <div>
              <div className="text-sm font-bold">
                {isArabic ? 'مكافآت الإحالة' : 'Referral rewards'}
              </div>
              <div className="text-[10px] text-muted-foreground">
                {isArabic
                  ? 'بدعوة الأصدقاء تكسب نقاطًا إضافية بعد إتمامهم 3 زيارات'
                  : 'Invite friends — earn bonus points after they complete 3 visits'}
              </div>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2 text-center">
            <div className="bg-secondary/20 rounded-lg p-2">
              <div className="text-[10px] text-muted-foreground">
                {isArabic ? 'دعوات' : 'Invites'}
              </div>
              <div className="text-2xl font-extrabold text-blue-400">
                {data.referral_stats.referred_count}
              </div>
            </div>
            <div className="bg-secondary/20 rounded-lg p-2">
              <div className="text-[10px] text-muted-foreground">
                {isArabic ? 'مُكتملة' : 'Completed'}
              </div>
              <div className="text-2xl font-extrabold text-emerald-400">
                {data.referral_stats.referred_completed}
              </div>
            </div>
            <div className="bg-secondary/20 rounded-lg p-2">
              <div className="text-[10px] text-muted-foreground">
                {isArabic ? 'نقاط ربحت' : 'Points earned'}
              </div>
              <div className="text-2xl font-extrabold text-amber-400">
                {data.referral_stats.points_earned_from_referrals}
              </div>
            </div>
          </div>
        </Card>
      )}

      {data.level.next && (
        <Card className="p-4 bg-card border-white/10">
          <div className="text-sm font-semibold mb-1">
            {isArabic ? 'مسارك للمستوى التالي' : 'Next level progress'}: {isArabic ? data.level.next.name_ar : data.level.next.name_en}
          </div>
          <div className="text-xs text-muted-foreground mb-2">
            {isArabic
              ? `باقي ${data.level.next.points_to_go} نقطة أو ${data.level.next.months_to_go} شهر`
              : `${data.level.next.points_to_go} pts or ${data.level.next.months_to_go} months to go`}
          </div>
          <div className="h-2 rounded-full bg-secondary overflow-hidden">
            <div
              className="h-full"
              style={{
                width: `${Math.min(100, Math.round((data.total_points_earned / Math.max(1, data.level.next.min_points)) * 100))}%`,
                background: data.level.color,
              }}
            />
          </div>
        </Card>
      )}

      {data.recent_transactions && data.recent_transactions.length > 0 && (
        <Card className="p-4 bg-card border-white/10">
          <h3 className="font-bold mb-2 flex items-center gap-2"><TrendingUp className="w-4 h-4" />{isArabic ? 'آخر العمليات' : 'Recent activity'}</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <tbody>
                {data.recent_transactions.map((t, i) => (
                  <tr key={`${t.created_at || ''}-${t.type}-${i}`} className="border-b border-white/5">
                    <td className="py-2 text-xs text-muted-foreground">{(t.created_at || '').slice(0, 10)}</td>
                    <td className={`py-2 font-semibold ${t.type === 'earn' ? 'text-emerald-500' : 'text-blue-500'}`}>
                      {t.type === 'earn' ? (isArabic ? 'كسب' : 'Earn') : (isArabic ? 'استبدال' : 'Redeem')}
                    </td>
                    <td className="py-2 text-muted-foreground text-xs">{t.service_name || '-'}</td>
                    <td className="py-2 font-bold text-end">{t.type === 'earn' ? '+' : '-'}{t.points}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Off-screen printable card sheet for PDF generation */}
      <div style={{ position: 'fixed', left: '-10000px', top: 0, pointerEvents: 'none' }} aria-hidden="true">
        <CardPrintSheet ref={printRef} data={data} isArabic={isArabic} />
      </div>
    </div>
  );
};

const StatBox = ({ label, value, className = '', icon: I = null }) => (
  <Card className="p-4 bg-card border-white/10">
    <div className="text-xs text-muted-foreground flex items-center gap-1">
      {I && <I className="w-3 h-3" />}
      {label}
    </div>
    <div className={`text-2xl font-bold ${className}`}>{value}</div>
  </Card>
);

// ---------- Page wrapper ----------

const LoyaltyPublic = () => {
  const { language } = useLanguage();
  const isArabic = language === 'ar';
  const [step, setStep] = useState(1);
  const [captchaData, setCaptchaData] = useState(null);
  const [card, setCard] = useState(null);
  const [statusInfo, setStatusInfo] = useState(null);

  // Track each public visit so the visitor counter below reflects real traffic
  useEffect(() => {
    trackVisit('/loyalty');
  }, []);

  const termsRef = useRef(null);
  const handlePrintTerms = useElementToPdf({
    contentRef: termsRef,
    documentTitle: 'KE_Gents_Saloon_Loyalty_Terms_AR_EN',
    pageStyle: '@page { size: A4 portrait; margin: 0; } @media print { body { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; margin: 0; padding: 0; } html, body { height: auto !important; } * { box-sizing: border-box; } }',
  });

  const reset = () => {
    setStep(1);
    setCaptchaData(null);
    setCard(null);
    setStatusInfo(null);
  };

  return (
    <div className="min-h-screen bg-background flex flex-col" dir={isArabic ? 'rtl' : 'ltr'}>
      <div className="px-4 pt-4">
        <BackToHome />
      </div>
      {/* Centered viewport: form/card stays pinned to the visible area without scrolling */}
      <div className="flex-1 w-full flex items-center justify-center px-4 py-4">
        {step === 1 && (
          <Step1Lookup
            isArabic={isArabic}
            onNext={(d) => { setCaptchaData(d); setStep(2); }}
            onPending={(info) => { setStatusInfo(info); setStep(4); }}
          />
        )}
        {step === 2 && captchaData && (
          <Step2Captcha
            isArabic={isArabic}
            captchaData={captchaData}
            onVerified={(d) => { setCard(d); setStep(3); }}
            onBack={reset}
          />
        )}
        {step === 3 && card && (
          <Step3Card isArabic={isArabic} data={card} onReset={reset} />
        )}
        {step === 4 && statusInfo && (
          <StepStatusCard isArabic={isArabic} info={statusInfo} onReset={reset} />
        )}
      </div>

      {/* Terms & Conditions download — pinned compactly at the bottom, doesn't push the card out of view */}
      <div className="w-full max-w-md mx-auto pb-3 px-4 flex flex-col items-center gap-1 shrink-0" dir={isArabic ? 'rtl' : 'ltr'}>
        <Button
          onClick={handlePrintTerms}
          variant="outline"
          size="sm"
          className="gap-2 border-primary/40 hover:bg-primary/10"
          data-testid="loyalty-terms-pdf-btn"
        >
          <FileText className="w-4 h-4" />
          {isArabic ? 'تحميل شروط وأحكام البطاقة (PDF)' : 'Download Loyalty Terms & Conditions (PDF)'}
        </Button>
      </div>

      {/* Public visitor counters — compact, no scroll */}
      <VisitorCounters isArabic={isArabic} />

      {/* Hidden printable terms sheet (off-screen so PDF generator can render) */}
      <div style={{ position: 'fixed', left: '-10000px', top: 0, pointerEvents: 'none' }} aria-hidden="true">
        <LoyaltyTermsSheet ref={termsRef} />
      </div>
    </div>
  );
};

export default LoyaltyPublic;

// ===== Compact visitor counters strip (7 buckets in a 4+3 grid, no scroll) =====
const VisitorCounters = ({ isArabic }) => {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.get('/public/visit-stats?page=/loyalty')
      .then((res) => { if (!cancelled) setStats(res.data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const buckets = [
    { key: 'today',     ar: 'اليوم',    en: 'Today',    color: '#10b981' },
    { key: 'week',      ar: 'أسبوع',    en: '7 days',   color: '#06b6d4' },
    { key: 'two_weeks', ar: 'أسبوعين',  en: '14 days',  color: '#3b82f6' },
    { key: 'month',     ar: 'شهر',      en: 'Month',    color: '#8b5cf6' },
    { key: 'quarter',   ar: 'ربع سنة',  en: 'Quarter',  color: '#d4af37' },
    { key: 'half_year', ar: 'نص سنة',   en: '6 months', color: '#f97316' },
    { key: 'year',      ar: 'سنة',      en: 'Year',     color: '#ef4444' },
  ];

  const fmt = (n) => {
    if (n == null) return '—';
    if (n >= 1000) return (n / 1000).toFixed(1).replace(/\.0$/, '') + 'K';
    return String(n);
  };

  return (
    <div className="w-full max-w-md mx-auto px-3 pb-3 shrink-0" dir={isArabic ? 'rtl' : 'ltr'}>
      <div className="flex items-center justify-center gap-1.5 mb-1.5">
        <Eye className="w-3 h-3 text-amber-400" />
        <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">
          {isArabic ? 'زوار صفحة الولاء' : 'Loyalty Page Visitors'}
        </span>
      </div>
      <div className="grid grid-cols-4 gap-1">
        {buckets.slice(0, 4).map((b) => (
          <CounterTile key={b.key} value={stats?.[b.key]} label={isArabic ? b.ar : b.en} color={b.color} fmt={fmt} />
        ))}
      </div>
      <div className="grid grid-cols-3 gap-1 mt-1">
        {buckets.slice(4).map((b) => (
          <CounterTile key={b.key} value={stats?.[b.key]} label={isArabic ? b.ar : b.en} color={b.color} fmt={fmt} />
        ))}
      </div>
    </div>
  );
};

const CounterTile = ({ value, label, color, fmt }) => (
  <div
    className="rounded-md border bg-card/50 backdrop-blur px-1 py-1.5 text-center"
    style={{ borderColor: `${color}40` }}
    data-testid={`visit-counter-${label}`}
  >
    <div className="text-base font-bold leading-none" style={{ color }}>
      {fmt(value)}
    </div>
    <div className="text-[9px] text-muted-foreground mt-0.5">{label}</div>
  </div>
);
